import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
} from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import type { PipelineStepDraftDto } from '@protocols/use-cases';
import { DialogDismissGuardDirective } from '@shared/ui';

import { PipelineStepEditorEffects } from '../../../effects/pipeline-step-editor.effects';

import { PipelineStepEditorComponent } from './pipeline-step-editor.component';

/**
 * Dialog host for `PipelineStepEditorComponent`.
 *
 * Bridges the shared `PipelineStepEditorEffects.confirmed$` onto the dialog's
 * close payload — closes with a `PipelineStepDraftDto` on save and `undefined`
 * on cancel. Backdrop click / Escape do not close the dialog directly:
 * `DialogDismissGuardDirective` routes them through the editor's `onCancel()`
 * (dirty-gated discard confirm, same as the Cancel button), so the dialog
 * always closes through the editor's own cancel flow. `canDeactivate()` covers
 * navigation-driven dismissal (browser back): the routed pipeline editor page
 * consults open dialogs before allowing the exit, and this host delegates the
 * decision to the editor's own gate.
 *
 * The wrapper depends on `PipelineStepEditorEffects` (not the facade) on
 * purpose: the consumer manages a single shared `PipelineStepEditorEffects`
 * instance, seeds the editor via `PipelineStepEditorEffects.initialize(...)`,
 * and observes the same instance for the editor's handoff. The editor's facade
 * is internal to `PipelineStepEditorComponent` and is not the consumer's to
 * own — facades are per-component (see UI-LAYER.md "Facade classes are not
 * re-usable").
 *
 * The wrapper does **not** seed state. The consumer (e.g. the future
 * `PipelineEditorComponent`) is required to call
 * `PipelineStepEditorEffects.initialize({ context, step? })` against the
 * shared effects instance *before* opening this dialog. Keeping seeding on the
 * consumer means there is one and only one seeding path regardless of mount
 * mode (dialog, route, inline).
 *
 * The wrapper declares **no providers**. The consumer must spread
 * `pipelineStepEditorProviders` and `pipelineStepDetailStoreProviders` at its
 * own `providers: []` and pass `injector: this.injector` to `MatDialog.open`
 * so the consumer's `PipelineStepEditorEffects` instance reaches the dialog's
 * injector chain. That shared effects instance is the handoff seam.
 *
 * The consumer must also make `labelStoreProviders` reachable on the same
 * injector chain (production consumers get this for free from
 * `pipelineEditorProviders`). The shared `LabelEffects` instance is what
 * keeps the embedded category editor's pending-label / label-override
 * ledger in sync with the rest of the editor tree.
 */
@Component({
  selector: 'ts-pipeline-step-editor-dialog',
  template: `<ts-pipeline-step-editor
    #editor
    tsDialogDismissGuard
    (dismissRequested)="editor.onCancel()"
  ></ts-pipeline-step-editor>`,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatDialogModule, PipelineStepEditorComponent, DialogDismissGuardDirective],
})
export class PipelineStepEditorDialogComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @ViewChild(PipelineStepEditorComponent)
  private editor: PipelineStepEditorComponent | undefined;

  constructor(
    @Optional()
    public readonly dialogRef: MatDialogRef<
      PipelineStepEditorDialogComponent,
      PipelineStepDraftDto | undefined
    > | null,
    private readonly effects: PipelineStepEditorEffects
  ) {}

  /** Exit gate consulted by the routed page host before navigation commits. */
  public canDeactivate(): Observable<boolean> | boolean {
    return this.editor ? this.editor.canDeactivate() : true;
  }

  ngOnInit(): void {
    this.effects.confirmed$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((outcome) => this.dialogRef?.close(outcome.kind === 'saved' ? outcome.step : undefined));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
